import { motion } from "framer-motion";
import { Container } from "@/components/common/Container";
import { AnimatedHeading } from "@/components/common/AnimatedHeading";
import { CtaButton } from "@/components/common/CtaButton";
import { Reveal } from "@/components/common/Reveal";
import { siteConfig } from "@/config/site.config";
import { EASE } from "@/animations/motion";
import type { LucideIcon } from "lucide-react";

interface DetailHeroProps {
  category: string;
  titleLines: string[];
  tagline: string;
  icon: LucideIcon;
  accent: string;
  image?: string;
}

/** Shared premium hero for service & solution detail pages. */
export function DetailHero({
  category,
  titleLines,
  tagline,
  icon: Icon,
  accent,
  image
}: DetailHeroProps) {
  return (
    <section className="relative overflow-hidden pb-14 pt-36 sm:pt-40 lg:pb-20 lg:pt-44">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div
          className="absolute -top-40 left-1/2 h-[480px] w-[820px] -translate-x-1/2 rounded-full blur-[130px]"
          style={{ background: `${accent}1f` }}
        />
        <div className="absolute inset-0 bg-grid opacity-50" />
      </div>

      <Container>
        {/* <div className="grid items-center gap-12 lg:grid-cols-[1.1fr_0.9fr]"> */}
        <div className="grid items-center gap-12 lg:grid-cols-[0.95fr_1.05fr] xl:grid-cols-[0.9fr_1.1fr]">
          <div>
            <Reveal>
              <span
                className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3.5 py-1.5 text-xs font-medium uppercase tracking-[0.14em]"
                style={{ color: accent }}
              >
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ background: accent }}
                />
                {category}
              </span>
            </Reveal>
            <AnimatedHeading
              lines={titleLines}
              className="mt-6 max-w-2xl font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-foreground sm:text-4xl lg:text-[3rem]"
            />
            <Reveal delay={0.15}>
              <p className="mt-6 max-w-xl text-lg text-muted-foreground">
                {tagline}
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <CtaButton to={siteConfig.primaryCta.path} size="lg" withArrow magnetic>
                  Book a Free Consultation
                </CtaButton>
                <CtaButton to={siteConfig.primaryCta.path} size="lg" variant="outline">
                  Request a Proposal
                </CtaButton>
              </div>
            </Reveal>
          </div>

          {/* Signature icon medallion */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
            className="relative hidden justify-self-end lg:block"
          >
            {/* <div className="relative flex h-64 w-84 items-center justify-center overflow-hidden rounded-[2rem] border border-border bg-card shadow-[0_40px_100px_-40px_rgba(15,23,42,0.35)]"> */}
            <div className="relative flex aspect-[16/10] w-full max-w-[760px] items-center justify-center overflow-hidden rounded-[2rem] border border-border bg-card shadow-[0_40px_100px_-40px_rgba(15,23,42,0.35)]">  
              <div
                className="absolute inset-0 rounded-[2rem] opacity-10"
                style={{ background: `radial-gradient(circle at 30% 20%, ${accent}, transparent 70%)` }}
              />
              <div className="absolute inset-0 rounded-[2rem] bg-grid opacity-40" />
              {
                  image? (
                    <img
                      src={image}
                      alt={tagline}
                      className="h-full w-full object-cover object-center rounded-[2rem]"
                    />
                  ) : (
                    <span
                      className="relative flex h-24 w-24 items-center justify-center rounded-3xl text-white shadow-lg"
                      style={{ background: accent }}
                    >
                      <Icon className="h-11 w-11" />
                    </span>
                  )
                }
            </div>
          </motion.div>
        </div>
      </Container>
    </section>
  );
}
